import React, { useState, useEffect } from "react";
import { RootState } from "../../app/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { FormField, Toggle, SpaceBetween } from "@amzn/awsui-components-react";
import { StringField } from "../searchComponents";
import {
  PropertyFilter,
  PropertyFilterGroup,
  setActivePropertyFiltersVisibilityEnabled,
  setQueryPropertyFilters,
} from "../searchSideBarConnector";
import { PropertyFilterInput } from "../../filterBar/PropertyFilterInput";
import "./SearchPropertyFilter.css";

/**
 * Legacy-Route filter panel that combines three controls in a vertical
 * `SpaceBetween`:
 *
 * 1. An "ASIN" textarea for entering ASIN IDs directly.
 * 2. A "More attributes" `FormField` containing the shared
 *    `<PropertyFilterInput/>` (the Cloudscape `PropertyFilter` primitive
 *    plus polaris <-> Redux transformation, extracted so the App Shell
 *    Filter Bar can reuse it without the ASIN textarea and toggle).
 * 3. An "Auto-enable attribute visibility" toggle.
 *
 * All three controls read and write the existing `generateQuery` Redux
 * slice - this component owns no filter state of its own.
 */
const SearchPropertyFilter = () => {
  const dispatch = useDispatch();

  const [asinFilter, setAsinFilter] = useState("");
  const { queryPropertyFilters, activePropertyFiltersVisibilityEnabled } =
    useSelector((state: RootState) => state.generateQuery);

  // Sync the local ASIN textarea with any `asin` PropertyFilter that
  // arrives via URL hash / preset / other out-of-band update.
  useEffect(() => {
    const asinFromSlice = extractAsinFilter(queryPropertyFilters);
    setAsinFilter(asinFromSlice);
  }, [queryPropertyFilters]);

  const updateQueryFromAsinFilter = (asin: string): void => {
    // Preserve any existing attribute filters (PropertyFilterGroup
    // entries) and replace only the top-level `asin` PropertyFilter.
    const preserved = queryPropertyFilters?.filters?.filter(
      (f: PropertyFilter | PropertyFilterGroup) =>
        f.type === "PropertyFilterGroup"
    );
    const filters = [...(preserved ?? [])];
    filters.push({
      type: "PropertyFilter",
      operator: "filter",
      value: asin,
      propertyKey: "asin",
    });
    dispatch(
      setQueryPropertyFilters({
        type: "PropertyFilterGroup",
        filters,
        operation: "and",
      })
    );
  };

  return (
    <SpaceBetween size="s" className={"search-property-filter-form"}>
      <FormField id={"asin-form"} label="ASIN" stretch={true}>
        {StringField(
          "B004AT6YQN, B00CC8XHN9, B00CUTCNXM, B00KBNMT06 ...",
          "asin",
          asinFilter as string,
          (event: any) => {
            setAsinFilter(event?.payload?.queryState?.value);
            updateQueryFromAsinFilter(event?.payload?.queryState?.value);
          }
        )}
      </FormField>
      <FormField
        id={"property-filter-form"}
        label="More attributes"
        stretch={true}
      >
        {/*
          Hide ASIN from the property-filter dropdown here because
          Legacy already owns ASIN via the dedicated textarea above.
          Without this, a Legacy user could enter conflicting ASIN
          tokens from both places and produce `ASIN = X AND ASIN = Y`
          (empty result). The App Shell FilterBar passes no
          `omitProperties` so ASIN is a first-class filter there.
        */}
        <PropertyFilterInput omitProperties={["asin"]} />
      </FormField>
      <FormField
        id={"property-filter-visibility"}
        label="Auto-enable attribute visibility"
        stretch={true}
      >
        <Toggle
          checked={activePropertyFiltersVisibilityEnabled}
          onChange={({ detail }) => {
            dispatch(setActivePropertyFiltersVisibilityEnabled(detail.checked));
          }}
        />
      </FormField>
    </SpaceBetween>
  );
};

/**
 * Reads any top-level `asin` PropertyFilter from a `PropertyFilterGroup`
 * and returns its raw string value. Returns an empty string when the
 * group has no `asin` entry - so the ASIN textarea clears itself when
 * a filter update removes the ASIN portion.
 */
function extractAsinFilter(group: PropertyFilterGroup | undefined): string {
  if (!group || !group.filters || group.filters.length === 0) return "";
  for (const f of group.filters) {
    if (f.type === "PropertyFilter" && f.propertyKey === "asin") {
      return typeof f.value === "string" ? f.value : "";
    }
  }
  return "";
}

export default SearchPropertyFilter;
